import { Rider, Vehicle } from '@interfaces/models.interface';

/**
 * Wording and grouping for the Partners tab (Partner List + Summary).
 *
 * The API deliberately sends keys rather than labels, so every string a partner
 * row or summary card shows is decided here.
 */

export type PartnerVehicleClass = 'two_wheel' | 'four_wheel' | 'truck' | 'unknown';

/** Values of the backend PartnerVehicleClassFilter / PartnerServiceFilter enums. */
export const PARTNER_VEHICLE_CLASS_FILTERS = ['TWO_WHEEL', 'FOUR_WHEEL', 'TRUCK'] as const;
export const PARTNER_SERVICE_FILTERS = [
  'EXPRESS_TWO_WHEEL',
  'MOTOTAXI',
  'TNVS',
  'TAXI',
  'EXPRESS_FOUR_WHEEL',
  'TRUCK',
] as const;

export type PartnerVehicleClassFilter = (typeof PARTNER_VEHICLE_CLASS_FILTERS)[number];
export type PartnerServiceFilter = (typeof PARTNER_SERVICE_FILTERS)[number];

export const PARTNER_VEHICLE_CLASS_LABELS: Record<PartnerVehicleClass, string> = {
  two_wheel: '2-Wheel',
  four_wheel: '4-Wheel',
  truck: 'Truck',
  unknown: 'No vehicle yet',
};

export const PARTNER_VEHICLE_CLASS_ICONS: Record<PartnerVehicleClass, string> = {
  two_wheel: 'riders.svg',
  four_wheel: 'supply.svg',
  truck: 'truck.svg',
  unknown: 'riders.svg',
};

export const PARTNER_SERVICE_LABELS: Record<string, { name: string; subtitle: string; icon: string }> = {
  express_two_wheel: {
    name: 'Motor Express',
    subtitle: 'OK! Express · motorcycle',
    icon: 'express-icon.png',
  },
  mototaxi: {
    name: 'MotoTaxi',
    subtitle: 'OK! MotoTaxi · motorcycle',
    icon: 'mototaxi-icon.png',
  },
  tnvs: {
    name: 'TNVS',
    subtitle: 'OK! Ride · 4- and 6-seater',
    icon: 'ride-icon.png',
  },
  taxi: {
    name: 'Taxi',
    subtitle: 'OK! Taxi · fleet and independent',
    icon: 'ride-icon.png',
  },
  express_four_wheel: {
    name: '4-Wheel Express',
    subtitle: 'OK! Express · car and van',
    icon: 'express-icon.png',
  },
  truck: {
    name: 'Trucks',
    subtitle: 'OK! Express · truck class',
    icon: 'express-icon.png',
  },
};

/** Rows inside a vehicle card on the Summary tab. */
export const PARTNER_VEHICLE_MIX_LABELS: Record<string, { label: string; dot: string }> = {
  express_only: { label: 'Express only', dot: 'veh-dot-express' },
  mototaxi_only: { label: 'MotoTaxi only', dot: 'veh-dot-mototaxi' },
  express_and_mototaxi: { label: 'Express + MotoTaxi', dot: 'veh-dot-combo' },
  tnvs_only: { label: 'TNVS only', dot: 'veh-dot-tnvs' },
  tnvs_and_express: { label: 'TNVS + Express', dot: 'veh-dot-combo' },
  taxi: { label: 'Taxi', dot: 'veh-dot-taxi' },
  none: { label: 'No service yet', dot: 'veh-dot-express' },
};

/**
 * Which vehicle a partner is registered with.
 *
 * Reads the vehicle catalogue, the same way the API classifies partners for the
 * summary counts and the vehicle/service filters (seat_count is the
 * catalogue's two-wheel marker; a truck is recognised by category name).
 * tbl_partners.vehicleType is not used: it is free text ("Motorcycle",
 * "motorcycle", "Pickup", ""), so it cannot be grouped.
 *
 * Only the label on screen depends on this — filtering is done by the API — so
 * the two can never disagree about which partners are listed.
 */
export function partnerVehicleClass(partner: Partial<Rider>): PartnerVehicleClass {
  const vehicles = partner?.partnerVehicles ?? [];
  const categories = vehicles.map((v) => (v?.vehicle?.category ?? '').toLowerCase());

  if (categories.some((category) => category.includes('truck'))) return 'truck';

  const seats = vehicles
    .map((v) => v?.vehicle?.seatCount)
    .filter((count): count is number => typeof count === 'number');

  if (seats.length === 0) return 'unknown';
  return Math.min(...seats) <= 2 ? 'two_wheel' : 'four_wheel';
}

export function partnerVehicleLabel(partner: Partial<Rider>): string {
  return PARTNER_VEHICLE_CLASS_LABELS[partnerVehicleClass(partner)];
}

/**
 * Service tags for the Approved Services column, as the union over the
 * partner's vehicles. Only approved partners carry these, so an empty list
 * renders as an em dash rather than an empty cell.
 */
export function partnerServiceTags(
  partner: Partial<Rider>,
): { label: string; className: string }[] {
  const vehicles: Vehicle[] = partner?.partnerVehicles ?? [];
  const holds = (pick: (v: Vehicle) => boolean | undefined) =>
    vehicles.some((vehicle) => pick(vehicle) === true);

  const vehicleClass = partnerVehicleClass(partner);
  const tags: { label: string; className: string }[] = [];

  if (holds((v) => v.isExpress)) {
    tags.push({
      label: vehicleClass === 'truck' ? 'Express · Truck' : 'Express',
      className: 'svc-tag-express',
    });
  }
  if (holds((v) => v.isMototaxi)) {
    tags.push({ label: 'MotoTaxi', className: 'svc-tag-mototaxi' });
  }
  if (holds((v) => v.isOkRide)) {
    tags.push({ label: 'TNVS', className: 'svc-tag-tnvs' });
  }
  if (holds((v) => v.isTaxi)) {
    tags.push({ label: 'Taxi', className: 'svc-tag-taxi' });
  }

  return tags;
}

const PARTNER_STATUS_PRESENTATION: Record<string, { label: string; className: string }> = {
  approved: { label: 'Approved', className: 'stat-approved' },
  for_approval: { label: 'Under Review', className: 'stat-review' },
  for_review: { label: 'Under Review', className: 'stat-review' },
  for_admin_approval: { label: 'Under Review', className: 'stat-review' },
  for_additional_document_submission: { label: 'Under Review', className: 'stat-review' },
  for_resubmission: { label: 'Under Review', className: 'stat-review' },
  for_ammendment: { label: 'For Amendment', className: 'stat-amend' },
  initial_registered: { label: 'With Documents', className: 'stat-initial' },
};

export function partnerStatusLabel(status: string): string {
  return PARTNER_STATUS_PRESENTATION[status]?.label ?? status ?? '—';
}

export function partnerStatusClass(status: string): string {
  return PARTNER_STATUS_PRESENTATION[status]?.className ?? 'stat-initial';
}

/**
 * Partner dates do not all arrive in the same shape: `dateCreated` is a
 * Date scalar ("2026-10-06 04:48:05") while `dateApprove` is a plain String
 * field that serialises to epoch milliseconds. Both are accepted here rather
 * than changing `dateApprove`'s scalar, which the partner app already reads.
 */
export function parsePartnerDate(value: string | number | null | undefined): Date | null {
  if (value == null || value === '') return null;

  const asString = String(value);
  if (/^\d{10,}$/.test(asString)) {
    const fromEpoch = new Date(Number(asString));
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  // "2026-10-06 04:48:05" is UTC but carries no zone marker, so say so
  // explicitly. A bare "2026-10-06" is already parsed as UTC and must be left
  // alone — appending Z to it produces an invalid date.
  const hasTime = /\d{2}:\d{2}/.test(asString);
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(asString);
  const normalized =
    hasTime && !hasZone ? `${asString.replace(' ', 'T')}Z` : asString.replace(' ', 'T');

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
